/**
 * Plans Configuration — Internal Account Architecture
 * 
 * NOTE: Prices here are for INTERNAL account-type routing only.
 * Customer-facing pricing lives in src/lib/stacks/stackCatalog.ts (Stacks-only model).
 * 
 * Single source of truth for AccountType + Plan architecture.
 * All plan definitions, limits, and helper functions.
 */

import type { 
  AccountType, 
  PlanId, 
  PlanDefinition, 
  FeatureFlag,
  PartnerTrack,
  Track 
} from "./types";
export * from "./types";
export * from "./credits";
export { usePlanLimits, usePartnerTracks } from "./usePlanLimits";

/**
 * All plan definitions
 */
export const PLANS: Record<PlanId, PlanDefinition> = {
  // ═══════════════════════════════════════════════════════════════
  // TRACK: START (Individual users & small teams)
  // ═══════════════════════════════════════════════════════════════
  freemium: {
    id: "freemium",
    track: "start",
    name: "Free",
    nameNl: "Free",
    accountType: "START",
    priceEur: 0,
    subAccountLimit: 5,
    features: ["hq_dashboard_basic", "templates_basic"],
    description: "Get started for free",
    descriptionNl: "Gratis beginnen",
  },
  core_30: {
    id: "core_30",
    track: "start",
    name: "Core",
    nameNl: "Core",
    accountType: "START",
    priceEur: 99,
    subAccountLimit: 30,
    features: ["hq_dashboard", "templates", "automations_basic"],
    description: "200 credits/month, Revenue Suite",
    descriptionNl: "200 credits/maand, Revenue Suite",
  },
  pro_50: {
    id: "pro_50",
    track: "start",
    name: "Pro",
    nameNl: "Pro",
    accountType: "START",
    priceEur: 299,
    subAccountLimit: 50,
    features: ["hq_dashboard", "templates", "automations_pro", "reporting_basic"],
    description: "600 credits/month, Revenue + Demand Suite",
    descriptionNl: "600 credits/maand, Revenue + Demand Suite",
  },
  agency_80: {
    id: "agency_80",
    track: "start",
    name: "Agency",
    nameNl: "Agency",
    accountType: "START",
    priceEur: 500,
    subAccountLimit: 80,
    features: [
      "hq_dashboard", 
      "templates", 
      "automations_pro", 
      "reporting_pro", 
      "client_portal_basic"
    ],
    description: "1100 credits/month, All Suites",
    descriptionNl: "1100 credits/maand, Alle Suites",
  },

  // ═══════════════════════════════════════════════════════════════
  // TRACK: PARTNERS (B2B2X - managing multiple clients)
  // ═══════════════════════════════════════════════════════════════
  partner_operator: {
    id: "partner_operator",
    track: "partners",
    name: "Partners HQ — Operator",
    nameNl: "Partners HQ — Operator",
    accountType: "PARTNER_HQ",
    priceEur: 499,
    subAccountLimit: -1, // Unlimited
    features: ["partner_hq", "delivery_tools", "multi_client_switcher"],
    description: "Delivery focus, unlimited clients, custom credits",
    descriptionNl: "Delivery focus, onbeperkt klanten, maatwerk credits",
  },
  partner_reseller: {
    id: "partner_reseller",
    track: "partners",
    name: "Partners HQ — Reseller",
    nameNl: "Partners HQ — Reseller",
    accountType: "PARTNER_HQ",
    priceEur: 499,
    subAccountLimit: -1, // Unlimited
    features: ["partner_hq", "multi_client_switcher", "billing_portal", "whitelabel_basic"],
    description: "Sales focus, billing & whitelabel, custom credits",
    descriptionNl: "Sales focus, billing & whitelabel, maatwerk credits",
  },
  partner_retainer: {
    id: "partner_retainer",
    track: "partners",
    name: "Partners HQ — Retainers",
    nameNl: "Partners HQ — Retainers",
    accountType: "PARTNER_HQ",
    priceEur: 499,
    subAccountLimit: -1, // Unlimited
    features: ["partner_hq", "multi_client_switcher", "sla_tools", "recurring_reporting"],
    description: "Service focus, SLA & recurring, custom credits",
    descriptionNl: "Service focus, SLA & recurring, maatwerk credits",
  },

  // ═══════════════════════════════════════════════════════════════
  // TRACK: ENTERPRISE (Large orgs with governance)
  // ═══════════════════════════════════════════════════════════════
  enterprise: {
    id: "enterprise",
    track: "enterprise",
    name: "Enterprise",
    nameNl: "Enterprise",
    accountType: "ENTERPRISE",
    priceEur: 0, // Custom pricing
    subAccountLimit: -1, // Unlimited / Custom
    features: ["sso_saml", "scim", "audit_logs", "data_retention", "custom_roles"],
    description: "Custom pricing, SSO, dedicated support",
    descriptionNl: "Maatwerk pricing, SSO, dedicated support",
  },
};

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Get plan definition by ID
 */
export function getPlan(planId: PlanId | string | null): PlanDefinition | null {
  if (!planId) return null;
  const normalized = normalizePlanId(planId);
  return PLANS[normalized] ?? null;
}

/**
 * Normalize legacy plan IDs to new format
 */
export function normalizePlanId(planId: string | null): PlanId {
  if (!planId) return "freemium";
  const lower = planId.toLowerCase();
  
  // Legacy mappings
  if (lower === "free" || lower === "start") return "freemium";
  if (lower === "core") return "core_30";
  if (lower === "pro" || lower === "guided" || lower === "managed" || lower === "fullsuite") return "pro_50";
  if (lower === "agency" || lower === "business") return "agency_80";
  if (lower === "operator") return "partner_operator";
  if (lower === "reseller") return "partner_reseller";
  if (lower === "retainer") return "partner_retainer";
  
  // Direct match
  if (lower in PLANS) return lower as PlanId;
  
  return "freemium";
}

/**
 * Get AccountType for a plan
 */
export function getAccountTypeForPlan(planId: PlanId | string | null): AccountType {
  const plan = getPlan(planId);
  return plan?.accountType ?? "START";
}

/**
 * Check if user can create more sub-accounts
 */
export function canCreateSubAccount(planId: PlanId | string | null, currentCount: number): boolean {
  const plan = getPlan(planId);
  if (!plan) return false;
  if (plan.subAccountLimit === -1) return true; // Unlimited
  return currentCount < plan.subAccountLimit;
}

/**
 * Get remaining sub-account slots
 * Returns -1 for unlimited
 */
export function getSubAccountsRemaining(planId: PlanId | string | null, currentCount: number): number {
  const plan = getPlan(planId);
  if (!plan) return 0;
  if (plan.subAccountLimit === -1) return -1; // Unlimited
  return Math.max(0, plan.subAccountLimit - currentCount);
}

/**
 * Check if plan has unlimited sub-accounts
 */
export function isUnlimited(planId: PlanId | string | null): boolean {
  const plan = getPlan(planId);
  return plan?.subAccountLimit === -1;
}

/**
 * Check if plan has a specific feature
 */
export function hasFeature(planId: PlanId | string | null, feature: FeatureFlag): boolean {
  const plan = getPlan(planId);
  return plan?.features.includes(feature) ?? false;
}

/**
 * Get upgrade path for a plan
 */
export function getUpgradePath(planId: PlanId | string | null): PlanId | null {
  const normalized = normalizePlanId(planId);
  
  const upgradePaths: Partial<Record<PlanId, PlanId>> = {
    freemium: "core_30",
    core_30: "pro_50",
    pro_50: "agency_80",
    agency_80: "partner_operator", // Or suggest Partners track
  };
  
  return upgradePaths[normalized] ?? null;
}

/**
 * Get all plans for a specific track
 */
export function getPlansByTrack(track: Track): PlanDefinition[] {
  return Object.values(PLANS).filter(p => p.track === track);
}

/**
 * Get all plans for a specific account type
 */
export function getPlansByAccountType(accountType: AccountType): PlanDefinition[] {
  return Object.values(PLANS).filter(p => p.accountType === accountType);
}

/**
 * Plan level for comparison (higher = more capabilities)
 */
export const PLAN_LEVELS: Record<PlanId, number> = {
  freemium: 1,
  core_30: 2,
  pro_50: 3,
  agency_80: 4,
  partner_operator: 5,
  partner_reseller: 5,
  partner_retainer: 5,
  enterprise: 10,
};

/**
 * Check if planA is at least planB level
 */
export function isAtLeastPlan(planA: PlanId | string | null, planB: PlanId): boolean {
  const normalizedA = normalizePlanId(planA);
  return PLAN_LEVELS[normalizedA] >= PLAN_LEVELS[planB];
}

/**
 * Get display name for plan
 */
export function getPlanDisplayName(planId: PlanId | string | null, locale: "en" | "nl" = "nl"): string {
  const plan = getPlan(planId);
  if (!plan) return locale === "nl" ? "Geen plan" : "No plan";
  return locale === "nl" ? plan.nameNl : plan.name;
}

/**
 * Get all plans as array (sorted by level)
 */
export function getAllPlans(): PlanDefinition[] {
  return Object.values(PLANS).sort((a, b) => PLAN_LEVELS[a.id] - PLAN_LEVELS[b.id]);
}

/**
 * Get partner tracks that a plan includes
 */
export function getPartnerTracksForPlan(planId: PlanId | string | null): PartnerTrack[] {
  const normalized = normalizePlanId(planId);
  
  if (normalized === "partner_operator") return ["operator"];
  if (normalized === "partner_reseller") return ["reseller"];
  if (normalized === "partner_retainer") return ["retainer"];
  
  return [];
}
